import React from "react";
import repartisLogo from "../../../assets/images/logo.svg";
import { fmtNum } from "./formatters";

// Phase 3 (M2): Report cover / front page — becomes PDF page 1.
//
// Unlike the section templates, the cover does NOT reuse A4PageFrame: its header
// shows the repartis company block (not the building name) and its footer adds
// the IBAN / bank / SWIFT line. It therefore renders its own A4 portrait page,
// but keeps A4PageFrame's outer contract (data-rr-page + .repartition-page +
// 794×1123) so the export orchestrator captures it identically and the
// PP Neue Montreal font cascades in without inline fontFamily.
//
// Props
//   building       raw building object (building_name, building_address,
//                  owner_name, property_address, managing_address)
//   periodLabel    string — "01.01.2024 - 31.12.2024"
//   periodYears    { current } — used in "Total charges <year>"
//   author         string — "Établi par : repartis SA – <author>"
//   reportDate     string — pre-formatted French long date
//   totalTtc       number — cost-summary totals.main_ttc
//   totalSurface   number — surfaces grand_total (m²)
//   whiteLabelling bool
//   logoUrl        string — per-period custom logo (when whiteLabelling)
//   pageNumber     number (1)
//   grandTotalPages number — total pages in the report

const A4_PORTRAIT = { width: 794, height: 1123 };

const FrontPage = ({
  building,
  periodLabel,
  periodYears,
  author,
  reportDate,
  totalTtc,
  totalSurface,
  whiteLabelling = false,
  logoUrl,
  pageNumber = 1,
  grandTotalPages = 1,
}) => {
  const isWhiteLabel = whiteLabellingEnabled(whiteLabelling);
  const staticUrl = process.env.REACT_APP_STATIC_URL || "";
  const customLogoSrc = isWhiteLabel && logoUrl ? `${staticUrl}${logoUrl}` : null;

  // Same 3-case logo behaviour as A4PageFrame:
  //   white-label OFF            → default repartis logo
  //   white-label ON + custom    → uploaded custom logo
  //   white-label ON + no custom → no logo
  let headerLogo = null;
  if (!isWhiteLabel) {
    headerLogo = <img src={repartisLogo} alt="Repartis" style={{ height: 28, width: "auto" }} />;
  } else if (customLogoSrc) {
    headerLogo = <img src={customLogoSrc} alt="Logo" style={{ height: 36, width: "auto" }} />;
  }

  const currentYear = periodYears?.current ?? "";

  // Owner / gérance value lines. property_address is the owner's billing address;
  // managing_address is the gérance line printed verbatim (per data model — there
  // is no separate gérance-name field).
  const ownerLine = [building?.owner_name, building?.property_address]
    .filter(Boolean)
    .join(" - ");
  const geranceLine = building?.managing_address || "";

  return (
    <div
      data-rr-page
      data-rr-section="Front Page"
      className="repartition-page"
      style={{
        width: A4_PORTRAIT.width,
        height: A4_PORTRAIT.height,
        boxSizing: "border-box",
        backgroundColor: "#ffffff",
        color: "#00033F",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        padding: 0,
        margin: 0,
      }}
    >
      {/* HEADER — company block (left) + logo (right) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "32px 48px 16px 48px",
          flexShrink: 0,
        }}
      >
        {!isWhiteLabel && (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#00033F" }}>repartis SA</div>
            <div style={{ fontSize: 10, color: "#4a4c6b", marginTop: 4 }}>
              Avenue Villamont 17, 1005 Lausanne
            </div>
            <div style={{ fontSize: 10, color: "#4a4c6b", marginTop: 2 }}>
              +41 58 255 43 43 - info@repartis.ch
            </div>
          </div>
        )}
        <div style={{ flexShrink: 0, marginLeft: "auto" }}>{headerLogo}</div>
      </div>

      {/* CONTENT */}
      <div
        style={{
          flex: 1,
          padding: "24px 48px",
          overflow: "hidden",
          color: "#00033F",
        }}
      >
        {/* Title */}
        <div
          style={{
            fontSize: 17,
            fontWeight: 600,
            textAlign: "center",
            marginBottom: 36,
          }}
        >
          Répartition des frais d'exploitation et d'entretien
        </div>

        {/* Detail block */}
        <div style={{ fontSize: 11, lineHeight: "20px", paddingLeft: 24 }}>
          <DetailGroup>
            <DetailLine label="Immeuble" value={building?.building_name} />
            <DetailLine label="Adresse" value={building?.building_address} />
          </DetailGroup>

          <DetailGroup>
            <DetailLine label="Période" value={periodLabel} />
            <DetailLine label="Propriétaire" value={ownerLine} />
            <DetailLine label="Gérance" value={geranceLine} />
          </DetailGroup>

          <DetailGroup>
            <DetailLine label="Établi par" value={`repartis SA – ${author || ""}`} />
            <DetailLine label="Date du rapport" value={reportDate} />
          </DetailGroup>

          <DetailGroup>
            <div style={{ fontWeight: 600 }}>Méthodologie :</div>
            <div>
              Répartition basée sur les surfaces, consommations et clés techniques, selon
              {" "}la solution core repartis.
            </div>
          </DetailGroup>

          <DetailGroup>
            <div style={{ fontWeight: 600 }}>Synthèse :</div>
            <div>
              Total charges {currentYear} : CHF {fmtNum(totalTtc)} TTC
            </div>
            <div>
              Surface totale : {fmtNum(totalSurface, { decimals: 0 })} m²
            </div>
          </DetailGroup>
        </div>
      </div>

      {/* FOOTER — page number (right). Bank account info (IBAN / Banque / SWIFT)
          removed per client request. */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          padding: "8px 48px 24px 48px",
          fontSize: 9,
          color: "#4a4c6b",
          flexShrink: 0,
        }}
      >
        {isWhiteLabel ? (
          <span>Powered by core repartis</span>
        ) : (
          <span />
        )}
        <span style={{ fontWeight: 600 }}>
          {isWhiteLabel ? "" : "repartis.ch | "}
          {grandTotalPages > 1 ? `Page ${pageNumber}/${grandTotalPages}` : `Page ${pageNumber || 1}`}
        </span>
      </div>
    </div>
  );
};

// A labelled "Label : value" line, matching the cover layout.
const DetailLine = ({ label, value }) => (
  <div>
    <span style={{ fontWeight: 600 }}>{label} :</span> {value || ""}
  </div>
);

// Vertical group with bottom spacing between detail blocks.
const DetailGroup = ({ children }) => (
  <div style={{ marginBottom: 18 }}>{children}</div>
);

// True only when the period explicitly opts in (mirrors A4PageFrame).
function whiteLabellingEnabled(v) {
  return v === true || v === "true";
}

export default FrontPage;
